import { useEffect, useState } from "react";

import { getAlbumGallery } from "../services/albumGalleryService";

const normalizeImage = (image, index) => ({
  ...image,
  imageId: image.imageId ?? image.id ?? index,
  imageUrl: image.imageUrl || image.signedUrl || image.url || null,
  displayOrder: Number(image.displayOrder ?? index),
});

export function useAlbumGallery(albumTag) {
  const [album, setAlbum] = useState(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!albumTag) {
      setAlbum(null);
      setImages([]);
      setLoading(false);
      setError("Invalid album.");
      return;
    }

    const controller = new AbortController();

    const load = async () => {
      setLoading(true);
      setError("");
      setAlbum(null);
      setImages([]);

      try {
        const response = await getAlbumGallery(albumTag, controller.signal);

        if (controller.signal.aborted) return;

        const data = response.data || {};

        const albumData = data.album || data.profile || null;
        const imageData = Array.isArray(data.images)
          ? data.images
          : Array.isArray(albumData?.images)
            ? albumData.images
            : [];

        const normalizedImages = imageData
          .map(normalizeImage)
          .filter((image) => image.imageUrl)
          .sort((a, b) => a.displayOrder - b.displayOrder);

        setAlbum(albumData);
        setImages(normalizedImages);
      } catch (error) {
        if (controller.signal.aborted) return;

        const message =
          error.response?.status === 403
            ? "You don't have access to this album."
            : error.response?.status === 404
              ? "Album not found."
              : error.response?.data?.message || "Unable to load album.";

        setError(message);
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    load();

    return () => controller.abort();
  }, [albumTag, refreshKey]);

  const refresh = () => setRefreshKey((value) => value + 1);

  return {
    album,
    images,
    loading,
    error,
    refresh,
  };
}
